/**
 * Game picker. `SHELL-020`, `021`, `022`, `026`, `027`.
 *
 * Reads each game's name, description and level count from
 * `@platform/game-registry`'s `gameInfo` rather than loading the module --
 * `SHELL-027`. The registry entry is a hand-maintained copy of what
 * `GameModule` itself carries, kept honest by `game-registry.test.ts`'s
 * per-game loop rather than by construction; see that registry's own header
 * for why this trade is worth making.
 *
 * `KNOWN_GAME_IDS` is already the stable order `SHELL-026` asks for --
 * `Object.keys` on a literal object in `game-registry.ts`, which cannot
 * reorder itself between sessions or even between calls in the same one.
 * Nothing here sorts it; sorting it would be solving a problem this file does
 * not have.
 *
 * `SHELL-022`/`023`/`024` (a selected game's module loading, and what happens
 * if it fails) are level select's from here on: this screen never starts a
 * load, so it has nothing to show while one is in flight or if one fails. See
 * `LevelSelect.tsx`.
 */

import { KNOWN_GAME_IDS, gameInfo } from '@platform/game-registry';
import { completionSummary } from '@shell/progression';
import { routes, APP_NAME } from '@shell/routes';
import { currentDocument } from '@shell/save';
import { Screen } from '@shell/Screen';

export function Picker() {
  // SHELL-021. Read once, not per tile: `Boot.tsx` does not mount `App`, and
  // so cannot mount this screen, before `bootState` is `ready`.
  const document = currentDocument();

  return (
    <Screen title={APP_NAME}>
      <ul class="game-list">
        {KNOWN_GAME_IDS.map((gameId) => {
          const info = gameInfo(gameId);
          // Unreachable: `gameId` is drawn from the same registry `gameInfo`
          // reads. The check exists for the type checker, not a real branch.
          if (!info) return null;

          const summary = completionSummary(info.levelCount, document.games[gameId]);

          return (
            <li key={gameId}>
              <a class="game-tile" href={routes.levelSelect(gameId)}>
                <span class="game-tile__name">{info.name}</span>
                <span class="game-tile__description">{info.description}</span>
                <span class="game-tile__progress">
                  {summary.completed} of {summary.total} levels complete
                </span>
              </a>
            </li>
          );
        })}
      </ul>
      <nav class="screen-nav">
        <a class="nav-link" href={routes.settings()}>
          Settings
        </a>
      </nav>
    </Screen>
  );
}
